import { ref, shallowRef, onScopeDispose, readonly } from 'vue';
import type { Ref, ShallowRef } from 'vue';
import type { Cable } from '@anycable/web';
import { createAppCable } from './createAppCable';
import {
  AppointmentsChannel,
  type TechnicianLocationPayload,
  type TechTrackingStateMessage,
  type TechTrackingRecord,
} from './AppointmentsChannel';

/**
 * Per-technician reactive tracking state, keyed in `techStates` by the wire
 * `technicianId` (which equals `AppointmentTechnician.id`, merged server-side).
 */
export interface TechState {
  position: { latitude: number; longitude: number };
  /**
   * Route ETA caption, or `null` when the wire omits it. LO-02: the map card
   * prefers this server-supplied eta as the label when it is non-null, and
   * falls back to its own client-side estimate otherwise.
   */
  eta: string | null;
  /**
   * The tech's route as a Google **encoded polyline string**, or `null` when
   * the tech has no stored route. Decoded + rendered by the map card.
   */
  routePolyline: string | null;
  /**
   * Ground speed in METERS PER SECOND from the device GPS, or `null` when the
   * frame omits it (e.g. the initial snapshot). Drives the client-side ETA.
   */
  speed: number | null;
  /**
   * Heading in degrees clockwise from true north (0–359), or `null` when the
   * frame omits it. Rotates the vehicle marker to face its direction of travel.
   */
  heading: number | null;
  /** Epoch ms of the last accepted frame — drives the freshness indicator. */
  lastSeenAt: number;
}

export interface UseAppointmentsTrackingOptions {
  /**
   * The `cable_token` (D-06). Accepts either a plain `string` (a setup-time
   * value) OR a getter `() => string`. The getter form fixes the cold-load race
   * (CR-01): `connect()` re-reads the CURRENT token at connect time rather than
   * a captured snapshot, so a `cable_token` that resolves AFTER the card mounts
   * still opens the subscription. Plain-string callers are unaffected.
   */
  token: string | (() => string);
  /**
   * The appointment this surface is tracking. Used as the
   * `AppointmentsChannel` subscription param (`{ id }`, server-scoped via
   * `stream_for(appointment)`). The channel transmits the initial snapshot and
   * every live update; no client-side appointment filter is needed.
   */
  appointmentId: number | string;
  /** When set, the cable refreshes its token via `/v1/users/cable_auth` (D-06). */
  refreshToken?: boolean;
  /** Defaults to `true`: connect synchronously in `setup()`. */
  autoConnect?: boolean;
}

/**
 * The reactive bridge from the AnyCable feed to Vue state
 * (TRACK-01/02/03/04). It subscribes to ONE channel on the cable:
 *
 *   • `AppointmentsChannel({ id })` (per-appointment, identity-independent) —
 *     now the SINGLE source (`Customers::Channel` was removed server-side). On
 *     subscribe the server `transmit`s a `tech_tracking_state` SNAPSHOT (seeded
 *     here for instant initial render); thereafter each `stream_for(appointment)`
 *     broadcast drives a live upsert via `technician-location`. The stream is
 *     already scoped to the one appointment, so no per-record filter is applied.
 *
 * Lifecycle discipline (spike landmine): the composable MUST run synchronously
 * in `setup()`/an effect scope so `onScopeDispose` binds at the top level — the
 * socket is then torn down on unmount and never outlives the component. Never
 * register teardown inside a callback.
 *
 * Reconnection is owned entirely by AnyCable's Monitor (TRACK-04): this
 * composable only MIRRORS connect/disconnect into `reconnecting`/`cableState`;
 * it starts no timer-driven retry loop of its own.
 */
/** Internal reactive context shared by the wiring helpers below. */
interface TrackingContext {
  cableState: Ref<string>;
  reconnecting: Ref<boolean>;
  /**
   * ME-01: flips true when the server `reject()`s the subscription — that
   * arrives as a CHANNEL `close`, NOT a cable disconnect, so it is independent
   * of `cableState` (a rejected subscription can coexist with a healthy cable).
   */
  subscriptionRejected: Ref<boolean>;
  techStates: Ref<Map<number, TechState>>;
  cable: ShallowRef<Cable | null>;
  appointmentsChannel: ShallowRef<AppointmentsChannel | null>;
  unsubscribes: Array<() => void>;
}

/** Mirror the cable's lifecycle into reactive state (TRACK-04, no retry loop). */
function wireCable(ctx: TrackingContext, c: Cable): void {
  ctx.unsubscribes.push(
    c.on('connect', () => {
      ctx.cableState.value = c.state;
      ctx.reconnecting.value = false;
    }),
  );
  ctx.unsubscribes.push(
    c.on('disconnect', () => {
      ctx.cableState.value = c.state;
      ctx.reconnecting.value = true;
    }),
  );
  // A `close` (including a `Rejected` reason) is mirrored as state, never a crash.
  ctx.unsubscribes.push(
    c.on('close', () => {
      ctx.cableState.value = c.state;
    }),
  );
}

/**
 * Upsert one tech record into the per-tech Map (TRACK-03 + ASVS V5). Treats the
 * coordinates as untrusted: a record with a non-finite lat/lng is skipped for
 * the marker (but a null/absent position is a valid "no marker yet" record we
 * simply don't seed). Reassigns a NEW Map so watchers fire.
 */
function upsertTech(
  ctx: TrackingContext,
  id: number,
  position:
    | {
        latitude: number;
        longitude: number;
        speed?: number | null;
        heading?: number | null;
      }
    | null
    | undefined,
  routePolyline: string | null,
  eta: string | null,
): void {
  // No usable position → nothing to place on the map this frame. Keep any
  // previously-seeded state untouched.
  if (!position) return;
  const { latitude, longitude } = position;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

  const next = new Map(ctx.techStates.value);
  next.set(id, {
    position: { latitude, longitude },
    eta,
    routePolyline,
    speed: typeof position.speed === 'number' ? position.speed : null,
    heading: typeof position.heading === 'number' ? position.heading : null,
    lastSeenAt: Date.now(),
  });
  ctx.techStates.value = next;
}

/**
 * SNAPSHOT seam (AppointmentsChannel `tech_tracking_state`, transmitted on
 * subscribe). Seed every tech that carries a position; a null position
 * contributes no marker this turn but a later live `technician-location`
 * upserts it. NO per-record appointment filter — the stream is already scoped
 * to this appointment server-side.
 */
function wireSnapshot(ctx: TrackingContext, ch: AppointmentsChannel): void {
  ctx.unsubscribes.push(
    ch.on('tech-tracking-state', (msg: TechTrackingStateMessage) => {
      const all = msg.technicians ?? [];
      for (const rec of all) {
        upsertTech(
          ctx,
          Number(rec.technicianId),
          rec.position,
          rec.routePolyline ?? null,
          etaFromRecord(rec),
        );
      }
    }),
  );
}

/**
 * LIVE seam (AppointmentsChannel). The per-appointment stream is already scoped
 * server-side, so every `technician-location` frame applies: upsert the tech's
 * position, carrying `routeMetadata.polyline` → `routePolyline` and
 * `routeMetadata.eta` → eta.
 */
function wireLive(ctx: TrackingContext, ch: AppointmentsChannel): void {
  ctx.unsubscribes.push(
    ch.on('technician-location', (msg: TechnicianLocationPayload) => {
      upsertTech(
        ctx,
        Number(msg.technicianId),
        msg.position,
        msg.routeMetadata?.polyline ?? null,
        msg.routeMetadata?.eta ?? null,
      );
    }),
  );
}

/**
 * ME-01: observe the CHANNEL-level `close`. The server `reject()`s the
 * subscription when the appointment is not in `active_customer_appointments`;
 * that surfaces as a channel `close`, NOT a cable disconnect. Flip ONLY
 * `subscriptionRejected` here — leave `cableState` to the cable-level mirror
 * (`wireCable`), so a healthy-but-rejected cable still reports a healthy state.
 */
function wireChannelRejection(
  ctx: TrackingContext,
  ch: AppointmentsChannel,
): void {
  ctx.unsubscribes.push(
    ch.on('close', () => {
      ctx.subscriptionRejected.value = true;
    }),
  );
}

/**
 * A snapshot record carries no ETA field today; the snapshot wire carries no
 * eta yet, so this returns `null` until the wire ships it. The forward-compat
 * field stays (LO-02 — the consumer prefers server eta when non-null).
 */
function etaFromRecord(_rec: TechTrackingRecord): string | null {
  // TODO(wire-eta): return _rec's eta once the snapshot wire ships the field.
  return null;
}

/**
 * WR-02: a real appointment id is required to subscribe. A missing id
 * (null/undefined/empty/0 sentinel) must NOT open a bogus
 * `stream_for(appointment 0)` the server would reject() — fail loud at the call
 * site instead. The call site coalesces a missing id to '' so this gate skips it.
 */
function hasRealAppointmentId(id: number | string): boolean {
  return id != null && id !== '' && id !== 0;
}

export function useAppointmentsTracking(opts: UseAppointmentsTrackingOptions) {
  const ctx: TrackingContext = {
    cableState: ref<string>('idle'),
    reconnecting: ref(false),
    subscriptionRejected: ref(false),
    techStates: ref(new Map<number, TechState>()),
    // Cable is not deeply reactive — shallowRef avoids proxying socket internals.
    cable: shallowRef<Cable | null>(null),
    appointmentsChannel: shallowRef<AppointmentsChannel | null>(null),
    unsubscribes: [],
  };

  /** CR-01: resolve the CURRENT token (getter is re-read at connect time). */
  function readToken(): string {
    return typeof opts.token === 'function' ? opts.token() : opts.token;
  }

  function connect(): void {
    if (ctx.cable.value) return;
    // CR-01: never open a subscription on an empty token — the cold-load race
    // seeds `cable_token: ''` and the real token lands later. The call-site
    // watch re-invokes connect() once a non-empty token resolves.
    const token = readToken();
    if (!token) return;
    // WR-02: never open a subscription without a real appointment id (a missing
    // id would `stream_for(appointment 0)` and the server would reject()).
    if (!hasRealAppointmentId(opts.appointmentId)) return;
    const c = createAppCable({
      token,
      refreshToken: opts.refreshToken,
    });
    ctx.cable.value = c;
    wireCable(ctx, c);

    // The single per-appointment channel: identity-independent, server-scoped
    // via `stream_for(appointment)`. Subscribe transmits the initial snapshot
    // and owns every live update going forward.
    const channel = new AppointmentsChannel({ id: opts.appointmentId });
    ctx.appointmentsChannel.value = channel;
    wireSnapshot(ctx, channel);
    wireLive(ctx, channel);
    wireChannelRejection(ctx, channel);
    c.subscribe(channel);

    ctx.cableState.value = c.state;
  }

  function teardown(): void {
    ctx.unsubscribes.splice(0).forEach((u) => u());
    ctx.cable.value?.disconnect();
    ctx.cable.value = null;
    ctx.appointmentsChannel.value = null;
  }

  if (opts.autoConnect !== false) {
    connect();
  }
  // MUST be top-level in setup()/effectScope — never inside a callback (landmine).
  onScopeDispose(teardown);

  return {
    techStates: readonly(ctx.techStates),
    reconnecting: readonly(ctx.reconnecting),
    cableState: readonly(ctx.cableState),
    subscriptionRejected: readonly(ctx.subscriptionRejected),
    connect,
  };
}
